/**
 * RGM Starter Studio · Lyrics Side Drawer (Phase 1B-live).
 *
 * Slide-in right panel matching the Mixer drawer's interaction.
 * Private draft lyrics — persists to `studio_projects.lyrics` on
 * blur / debounced save; NEVER auto-publishes to profile or the
 * song. Recording and playback continue while open. Scroll is
 * contained inside the panel via `overscroll-behavior: contain`
 * so the audio timeline never seeks accidentally.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

export default function LyricsDrawer({ open, value, onClose, onSave }) {
  const [text, setText] = useState(value || "");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const initialised = useRef(false);

  useEffect(() => {
    // Only sync from prop on open or when server value changes and
    // the artist hasn't been typing.
    if (open) {
      setText(value || "");
      initialised.current = true;
    }
  }, [open, value]);

  const persist = useCallback(async (next) => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }, [onSave]);

  const handleChange = (e) => {
    const next = e.target.value.slice(0, 20000);
    setText(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(next), 900);
  };
  const handleBlur = () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (text !== (value || "")) persist(text);
  };

  if (!open) return null;

  const savedLabel = saving ? "Saving…" : savedAt ? "Saved" : "";

  return (
    <>
      <div
        className="fixed inset-0 z-30 bg-black/40"
        onClick={onClose}
        data-testid="lyrics-scrim"
      />
      <aside
        className="fixed top-0 bottom-[76px] right-0 z-40 w-[22rem] max-w-[92vw] bg-[#0a0a10] border-l border-white/10 shadow-[-8px_0_24px_rgba(0,0,0,0.55)] flex flex-col"
        data-testid="studio-lyrics-drawer"
        role="dialog"
        aria-label="Lyrics"
      >
        <header className="flex items-center gap-2 px-4 h-12 border-b border-white/10">
          <span className="text-lg">📝</span>
          <p className="text-xs font-bold text-white/90 flex-1 truncate">Lyrics</p>
          <span className="text-[10px] text-gray-500 tabular-nums" data-testid="lyrics-saved-indicator">{savedLabel}</span>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xs ml-2"
            data-testid="lyrics-close-btn"
            aria-label="Close lyrics"
          >Close ✕</button>
        </header>
        <textarea
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={"[Verse 1]\nType your lyrics here…\n\n[Chorus]\n"}
          className="flex-1 w-full resize-none bg-transparent text-white text-base leading-relaxed p-4 outline-none placeholder:text-gray-600"
          style={{ overscrollBehavior: "contain", fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: "17px", lineHeight: "1.7" }}
          data-testid="lyrics-textarea"
          spellCheck
        />
        <footer className="px-4 py-2 border-t border-white/10 text-[10px] text-gray-500">
          Private draft · saved with this Studio session · <span className="tabular-nums">{text.length}/20000</span>
        </footer>
      </aside>
    </>
  );
}
